/**
 * PhotoCapture — file-input + drag/drop zone for vehicle photos.
 *
 * Accepts: image/jpeg, image/png, image/webp, image/heic.
 * Max 25 MiB validated client-side (backend also enforces).
 * Multiple files: each file produces one queue item and one tile.
 *
 * No `capture` attribute — the native chooser offers both camera and
 * photo library (capture="environment" would force the camera).
 *
 * Cloned from InvoiceCapture with deltas:
 *   - Accept image/* only (no PDF)
 *   - multiple={true}
 *   - onFiles callback (plural) — one call per valid file
 *   - Aria label matches photo context
 *
 * Spec 011 — `multiple={false}` (before/after slots) restricts the native
 * picker to one file and truncates a multi-file drag-drop to the first;
 * `ariaLabel`/`label` let a slot say "Add before photo" instead of the
 * generic album copy, since two slots with the identical default label would
 * be indistinguishable to a screen reader on the same form.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { Camera } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';

interface PhotoCaptureProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Default true — every existing (album) caller is unaffected. */
  multiple?: boolean;
  ariaLabel?: string;
  label?: string;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_BYTES = 25 * 1024 * 1024; // 25 MiB

function isAllowedType(type: string): boolean {
  // HEIC files sometimes arrive with an empty type on some mobile browsers.
  // Accept empty type with a .heic extension as a best-effort pass-through.
  return ['image/jpeg', 'image/png', 'image/webp', 'image/heic', ''].includes(type);
}

function isDragEvent(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes('Files');
}

export function PhotoCapture({
  onFiles,
  disabled = false,
  multiple = true,
  ariaLabel = 'Add photos of your vehicle',
  label = 'Add photos',
}: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFiles = useCallback(
    (files: File[]) => {
      setValidationError(null);
      // Single-file mode (spec 011 slots): only ever take the first file,
      // whether it arrived via the picker (already limited by `multiple`
      // below) or a multi-file drag-drop (which isn't).
      const candidates = multiple ? files : files.slice(0, 1);
      const valid: File[] = [];
      for (const file of candidates) {
        if (!isAllowedType(file.type)) {
          setValidationError('Please upload JPEG, PNG, WEBP, or HEIC images only.');
          return;
        }
        if (file.size > MAX_BYTES) {
          setValidationError(`"${file.name}" is too large. Maximum size is 25 MB per photo.`);
          return;
        }
        valid.push(file);
      }
      if (valid.length > 0) onFiles(valid);
    },
    [onFiles, multiple],
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) handleFiles(files);
    e.target.value = '';
  }

  function handleDragOver(e: React.DragEvent) {
    if (!isDragEvent(e) || disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFiles(files);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }

  const zoneStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.sm,
    padding: `${SPACE.lg}px ${SPACE.md}px`,
    border: `2px dashed ${isDragOver ? T.accent : T.border}`,
    borderRadius: RADIUS.lg,
    background: isDragOver ? T.cardAlt : 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: `background ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
    outline: 'none',
    opacity: disabled ? 0.6 : 1,
    minHeight: 140,
  };

  return (
    <>
      {/* No `capture` attribute — the mobile chooser offers both camera and
          photo library (capture would force the camera, hiding the library). */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        aria-hidden="true"
        tabIndex={-1}
        style={{ display: 'none' }}
        onChange={handleChange}
        disabled={disabled}
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        style={zoneStyle}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
      >
        <Camera size={40} style={{ color: T.textMuted }} aria-hidden="true" />

        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              color: T.text,
              fontFamily: F.body,
            }}
          >
            {isDragOver ? 'Drop to add photos' : label}
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 12,
              color: T.textMuted,
              fontFamily: F.body,
            }}
          >
            JPEG, PNG, WEBP, or HEIC · up to 25 MB each
          </p>
        </div>
      </div>

      {validationError ? (
        <p role="alert" style={{ color: T.red, fontSize: 12, margin: `${SPACE.sm}px 0 0` }}>
          {validationError}
        </p>
      ) : null}
    </>
  );
}
